// Shared milestone rules for the time loggers (QuickTimeLog + My Time).
//
// Hours logged against a SOW with no milestone cost against nothing. They never
// reach that project's actuals, and they surface later on the Time Entry Audit
// as a gap somebody else has to chase and guess at, weeks after the person who
// could have answered has forgotten. The one moment anybody reliably knows which
// milestone an hour belongs to is the moment they are logging it.
//
// So where the project has milestones to pick from, both screens ask. Not
// picking one stays possible, because there are honest reasons for it —
// mobilisation before the first milestone opens, or work that genuinely spans
// the whole engagement — but the reason goes in the notes, so the entry explains
// itself to whoever reads the audit rather than becoming their problem.
//
// It lives here, and not inline in the quick panel, for the reason the "Other"
// note rule does: otherwise which screen you happened to open would decide
// whether the rule applied.

import { isMilestoneDone } from './milestoneStatus.js';

/**
 * Statuses that take a milestone off the list entirely: it was never a choice,
 * so nothing should be costed against it. Mirrors MILESTONE_NOT_ON_OFFER in
 * src/api/timeEntryAudit.js, which decides whether a no-milestone entry counts
 * as a gap at all. A test pins the two together — if they drift, the audit
 * chases entries the logger was never offered the chance to assign.
 */
export const MILESTONE_NOT_ON_OFFER = ['Cancelled', 'Split', 'Credit Noted'];

/** Shown when a no-milestone entry on a milestoned project has no note. Both loggers use this wording. */
export const NO_MILESTONE_REASON_MESSAGE =
  'This project has milestones. Pick the one this time was for, or say in the notes why it belongs to none of them.';

const onOffer = (m) => !MILESTONE_NOT_ON_OFFER.includes(m?.status);

/**
 * The milestones this project can actually take time against, with the ones
 * still being delivered first.
 *
 * Late time against a finished milestone is real and stays loggable, but it is
 * the exception, so the finished ones sit below rather than being hidden.
 * Order within each group is left as it arrived, since the API already returns
 * them by forecast date then sort order.
 *
 * @param {Array<{id: string|number, status?: string}>|null|undefined} milestones
 * @returns {Array<object>}
 */
export function offerableMilestones(milestones) {
  if (!Array.isArray(milestones)) return [];
  const live = milestones.filter(onOffer);
  return [...live.filter(m => !isMilestoneDone(m?.status)), ...live.filter(m => isMilestoneDone(m?.status))];
}

/**
 * The same list, split so a picker can show the finished ones apart and
 * visually demoted.
 *
 * @returns {{open: Array<object>, done: Array<object>}}
 */
export function groupMilestonesForPicker(milestones) {
  const live = Array.isArray(milestones) ? milestones.filter(onOffer) : [];
  return {
    open: live.filter(m => !isMilestoneDone(m?.status)),
    done: live.filter(m => isMilestoneDone(m?.status)),
  };
}

/**
 * Is there a milestone question to answer on this project at all?
 *
 * A SOW with no milestones, or only cancelled ones, has nothing to pick, and
 * warning about it would be noise on every entry.
 */
export function milestoneExpected(milestones) {
  return offerableMilestones(milestones).length > 0;
}

/**
 * Should this entry be refused for want of a reason?
 *
 * True only when the project has milestones on offer, none was picked, and the
 * notes are empty. A list that has not loaded yet is treated as no milestones:
 * refusing an entry because a fetch is outstanding would block a legitimate log.
 *
 * Ids are compared as strings, because a <select> holds its value as one while
 * the API returns numbers for some tables, and === on the mixed pair silently
 * never fires.
 */
export function missingMilestoneReason({ milestones, milestoneId, notes }) {
  if (!milestoneExpected(milestones)) return false;
  if (milestoneId != null && String(milestoneId) !== '') return false;
  return !(notes || '').trim();
}

export default missingMilestoneReason;
